<template>
  <div class="review-comments-container card">
    <h6 class="review-comments-title text-body">
      {{ computedLabel }}
    </h6>

    <div v-if="allComments.length" class="mb-2">
      <div
        v-for="(c, idx) in allComments"
        :key="idx"
        class="review-comment-item"
      >
        <p class="text-body">{{ c.text }}</p>
        <ul v-if="c.files && c.files.length">
          <li v-for="(f, fIdx) in c.files" :key="fIdx">
            <span
              v-if="isBlobOrEmpty(f)"
              class="file-link file-link--pending"
              v-b-tooltip.hover.d0
              :title="pendingFileTooltip"
            >{{ f.name || 'Archivo' }}</span>
            <a
              v-else
              :href="getFileHref(f)"
              target="_blank"
              class="file-link"
            >{{ f.name || 'Archivo' }}</a>
          </li>
        </ul>
        <small class="text-body">{{ c.user }} - {{ formatDate(c.date) }}</small>
        <hr v-if="idx < allComments.length - 1" />
      </div>
    </div>

    <div v-if="!disabled">
      <b-form-textarea
        v-model="newCommentText"
        rows="2"
        class="mb-2"
        :placeholder="tf('risk.add_comment', 'Agregar comentario')"
        :disabled="addingComment"
      />
      <div class="file-upload-section">
        <div 
          class="file-upload-info-box"
          v-b-tooltip.hover.html
          :title="fileFormatsTooltip"
        >
          <feather-icon icon="InfoIcon" size="14" class="info-icon" />
          <small class="file-upload-info-text">
            <strong>{{ tf('actions.file_upload.accepted_types_label', 'Tipos aceptados:') }}</strong> {{ tf('actions.file_upload.accepted_types_value', 'Imágenes, PDF, Word, Excel.') }}
            <strong>{{ tf('actions.file_upload.max_label', 'Máximo:') }}</strong> {{ tf('actions.file_upload.max_per_document', '5 MB por documento') }} • {{ tf('actions.file_upload.max_documents_total', '5 documentos en total') }}
          </small>
        </div>
        <b-form-file
          :key="fileInputKey"
          class="mt-2 file-input-custom"
          multiple
          :disabled="addingComment"
          @change="handleFileChange"
          :placeholder="tf('actions.file_upload.select_files', 'Seleccionar archivos...')"
          :browse-text="tf('actions.file_upload.browse', 'Examinar')"
        />
      </div>

      <div v-if="tempFilesPreview.length" class="mt-2 d-flex flex-wrap">
        <div
          v-for="(fileObj, idx) in tempFilesPreview"
          :key="idx"
          class="preview-container position-relative mr-2 mb-2"
        >
          <img
            v-if="isImage(fileObj.raw)"
            :src="fileObj.preview"
            alt="preview"
            class="preview-image"
          />
          <iframe
            v-else-if="isPdf(fileObj.raw)"
            :src="fileObj.preview"
            class="preview-pdf"
          />
          <b-button
            size="sm"
            variant="danger"
            class="remove-button"
            :disabled="addingComment"
            @click="removeTempFile(idx)"
          >
            ×
          </b-button>
        </div>
      </div>

      <b-button
        size="sm"
        variant="primary"
        class="mt-2"
        :disabled="addingComment"
        @click="addComment"
      >
        <b-spinner v-if="addingComment" small class="mr-1" />
        {{ addingComment ? (tf("actions.loading", "Guardando...") || "Guardando...") : tf("risk.add_comment", "Agregar comentario") }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { BButton, BFormTextarea, BFormFile, BSpinner } from "bootstrap-vue"
import moment from "moment"
import { getUserData } from "@/auth/utils"

export default {
  name: "RiskReviewCommentsWithPreview",
  components: { BButton, BFormTextarea, BFormFile, BSpinner },
  props: {
    id: { type: String, default: "" },
    labelToShow: { type: String, default: "" },
    existingComments: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    onUpdateComments: { type: Function, default: null },
    onAddComment: { type: Function, default: null },
  },
  data() {
    return {
      allComments: [],
      newCommentText: "",
      tempFiles: [],
      tempFilesPreview: [],
      fileInputKey: 0,
      maxFileSizeMB: 30,
      maxFilesCount: 5,
      addingComment: false,
    }
  },
  computed: {
    computedLabel() {
      return this.labelToShow || this.tf('risk.review_comments_title', 'Comentarios de revisión')
    },
    pendingFileTooltip() {
      return this.tf('review_comments.save_to_view', 'Guarde cambios para visualizar')
    },
    fileFormatsTooltip() {
      return this.tf('actions.file_upload.tooltip', 'Imágenes: JPG, JPEG, PNG, GIF, BMP, WEBP, SVG<br>Documentos: PDF<br>Word: DOC, DOCX, ODT, RTF<br>Excel: XLS, XLSX, CSV, ODS')
    }
  },
  watch: {
    existingComments: {
      immediate: true,
      handler(newVal) {
        this._revokeBlobUrlsInComments(this.allComments)
        this.allComments = Array.isArray(newVal) ? [...newVal] : []
      }
    }
  },
  mounted() {
    this.allComments = Array.isArray(this.existingComments) ? [...this.existingComments] : []
  },
  beforeDestroy() {
    this._revokeBlobUrlsInComments(this.allComments)
    this._revokeTempPreviews()
  },
  methods: {
    tf(key, fallback = "") {
      try {
        const out = this.$t ? this.$t(key) : key
        return typeof out === "string" && out === key ? fallback : out
      } catch (e) { return fallback }
    },
    formatDate(dt) { return moment(dt).format("DD/MM/YYYY HH:mm") },
    isImage(file) { return file?.type?.startsWith?.("image/") },
    isPdf(file) { return file?.type === "application/pdf" },
    isBlobOrEmpty(f) {
      const u = f?.url
      if (!u || String(u).trim() === "") return true
      return String(u).startsWith("blob:")
    },
    getFileHref(f) {
      if (f?.url && String(f.url).trim() !== "") return f.url
      return "#"
    },
    _revokeBlobUrlsInComments(comments) {
      (comments || []).forEach((c) => {
        (c.files || []).forEach((f) => {
          if (f?.url && typeof f.url === "string" && f.url.startsWith("blob:")) {
            try { URL.revokeObjectURL(f.url) } catch (_) {}
          }
        })
      })
    },
    _revokeTempPreviews() {
      (this.tempFilesPreview || []).forEach((p) => {
        if (p?.preview && typeof p.preview === "string" && p.preview.startsWith("blob:")) {
          try { URL.revokeObjectURL(p.preview) } catch (_) {}
        }
      })
    },
    handleFileChange(evt) {
      if (this.disabled) return
      const newFiles = Array.from(evt.target.files || [])
      
      const currentTotalFiles = this.tempFiles.length
      const maxAllowed = this.maxFilesCount
      
      if (currentTotalFiles >= maxAllowed) {
        if (this.$toast) {
          this.$toast.error(this.$t('actions.file_upload.max_documents_error', { n: maxAllowed }) || `Máximo ${maxAllowed} documentos permitidos`)
        }
        evt.target.value = ""
        return
      }

      const maxSizeBytes = this.maxFileSizeMB * 1024 * 1024
      const validFiles = []
      const errors = []

      newFiles.forEach(file => {
        if (file.size > maxSizeBytes) {
          errors.push(this.$t('actions.file_upload.max_size_error', { name: file.name, n: this.maxFileSizeMB }) || `${file.name}: excede el tamaño máximo de ${this.maxFileSizeMB} MB`)
          return
        }

        const willExceedLimit = currentTotalFiles + validFiles.length >= maxAllowed
        if (willExceedLimit) {
          errors.push(this.$t('actions.file_upload.max_documents_error', { n: maxAllowed }) || `Máximo ${maxAllowed} documentos permitidos`)
          return
        }

        validFiles.push(file)
      })

      if (errors.length > 0) {
        if (this.$toast) {
          errors.forEach(error => this.$toast.error(error))
        }
        evt.target.value = ""
      }

      validFiles.forEach(file => {
        const previewUrl = URL.createObjectURL(file)
        this.tempFiles.push(file)
        this.tempFilesPreview.push({ raw: file, preview: previewUrl })
      })

      if (validFiles.length === 0) {
        evt.target.value = ""
      }
    },
    removeTempFile(index) {
      const obj = this.tempFilesPreview[index]
      if (obj?.preview && typeof obj.preview === "string" && obj.preview.startsWith("blob:")) {
        try { URL.revokeObjectURL(obj.preview) } catch (_) {}
      }
      this.tempFiles.splice(index, 1)
      this.tempFilesPreview.splice(index, 1)
    },
    async addComment() {
      if (!this.newCommentText.trim() && !this.tempFiles.length) return

      if (typeof this.onAddComment === "function") {
        this.addingComment = true
        try {
          await this.onAddComment({
            label: this.id,
            text: this.newCommentText.trim(),
            files: [...this.tempFiles],
          })
          this._revokeTempPreviews()
          this.newCommentText = ""
          this.tempFiles = []
          this.tempFilesPreview = []
          this.fileInputKey += 1
        } catch (error) {
          if (this.$toast) {
            this.$toast.error(
              this.$t?.("risk.save_failed") || "No se pudo guardar el comentario"
            )
          }
        } finally {
          this.addingComment = false
        }
        return
      }

      const user = getUserData()?.email || "anonymous@domain"
      const newComment = {
        text: this.newCommentText.trim(),
        date: new Date().toISOString(),
        user,
        files: this.tempFiles.map(file => ({
          url: URL.createObjectURL(file),
          name: file.name,
          raw: file,
        })),
      }

      this.allComments.push(newComment)

      if (this.onUpdateComments) {
        this.onUpdateComments({ label: this.id, comments: this.allComments })
      }

      this._revokeTempPreviews()
      this.newCommentText = ""
      this.tempFiles = []
      this.tempFilesPreview = []
      this.fileInputKey += 1
    },
  },
}
</script>

<style scoped>
.review-comments-container { padding: 0.75rem; margin-top: 1rem; border: 1px solid #e5e7eb; border-radius: 8px; }
.review-comments-title { font-weight: 600; margin-bottom: 0.75rem; color: #374151; }
.preview-container { position: relative; }
.preview-image { max-width: 100px; max-height: 100px; border-radius: 4px; object-fit: cover; }
.preview-pdf { max-width: 100px; max-height: 100px; border: 1px solid #ddd; border-radius: 4px; }
.remove-button { position: absolute; top: -6px; right: -6px; border-radius: 50%; width: 22px; height: 22px; padding: 0; font-size: 16px; line-height: 1; }
.file-link { cursor: pointer; color: #2563eb; text-decoration: none; }
.file-link:hover { text-decoration: underline; }
.file-link--pending { cursor: not-allowed; color: #6c757d; }

.file-upload-section {
  margin-top: 0.5rem;
}

.file-upload-info-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  margin-bottom: 8px;
}

.info-icon {
  color: #0284c7;
  flex-shrink: 0;
  margin-top: 2px;
}

.file-upload-info-text {
  color: #0c4a6e;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

.file-upload-info-text strong {
  color: #075985;
  font-weight: 600;
}

.file-input-custom :deep(.custom-file-label) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 12px;
  background-color: #ffffff;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.file-input-custom :deep(.custom-file-label:hover) {
  border-color: #9ca3af;
}

.file-input-custom :deep(.custom-file-input:focus ~ .custom-file-label) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.file-input-custom :deep(.custom-file-label::after) {
  background-color: #3b82f6;
  border-color: #3b82f6;
  border-radius: 0 6px 6px 0;
  padding: 8px 16px;
  font-weight: 500;
}
</style>
